import { skipToken, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTRPC } from '@/api/query';
import { useSession } from '@/lib/session';
import { NAV_ITEMS } from './nav';

const PERSONAL = new Set(['individual', 'household']);
const silent = { meta: { silent: true } } as const;

/** Entities whose owner switched business features on (Settings → Entity), kept on the account. */
export const businessEnabledIds = (state: Record<string, unknown> | undefined): string[] => {
  const v = state?.businessEntityIds;
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
};

/**
 * Business (invoices, contacts) is hidden for personal entities until it's used — a contact or invoice exists — or
 * switched on in Settings → Entity. The routes stay reachable by URL.
 */
export const useBusinessVisible = (): boolean => {
  const trpc = useTRPC();
  const { entity, entityId, consolidated, user } = useSession();
  const personal = !consolidated && Boolean(entity && PERSONAL.has(entity.type));
  const enabled = Boolean(entityId && businessEnabledIds(user?.onboardingState).includes(entityId));
  const input = personal && !enabled && entityId ? { entityId } : skipToken;
  const contacts = useQuery(trpc.business.contacts.queryOptions(input, silent));
  const invoices = useQuery(trpc.business.invoices.queryOptions(input, silent));
  return !personal || enabled || Boolean(contacts.data?.length || invoices.data?.length);
};

export const useNavItems = () => {
  const business = useBusinessVisible();
  return useMemo(() => NAV_ITEMS.filter((i) => i.id !== 'business' || business), [business]);
};
